import axios from 'axios';
import { activeSiteKey, SiteKey } from './sites';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

/**
 * Eigener, bewusst "nackter" Client für die öffentliche Website.
 *
 * Nicht `services/api.ts` verwenden: Der hängt einen Auth-Token an und schickt
 * Besucher bei 401/403 auf die Login-Seite — beides hätte auf der Website nichts
 * zu suchen.
 */
const publicApi = axios.create({
  baseURL: `${API_BASE_URL}/api/public`,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
});

/** Welches Formular bzw. welche Seite die Anfrage ausgelöst hat. */
export type RequestTopic = 'CONTACT' | 'CARE_24H' | 'HOME_CARE' | 'COSTS' | 'CAREER';

/**
 * Der Auftritt, über den die Anfrage eingeht — die Namen des Enums `ContactRequestSite`
 * im Backend. Kommt ein Auftritt in `sites.json` dazu, verlangt der `Record` hier einen
 * Eintrag; er dort zu vergessen, fällt beim Übersetzen auf und nicht erst im Posteingang.
 */
export type RequestSite = 'ANTCZAK' | 'MITEINANDER38';

const REQUEST_SITE: Record<SiteKey, RequestSite> = {
  antczak: 'ANTCZAK',
  miteinander38: 'MITEINANDER38',
};

export interface ContactSubmission {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  message: string;
  topic: RequestTopic;
  sourcePath?: string;
  /**
   * Antworten auf die Zusatzfragen — nur bei den Anliegen, die sie stellen
   * (siehe `fragebogen.ts`). Unbeantwortete Fragen fehlen hier ganz.
   */
  details?: Record<string, string>;
  /** Honeypot: bleibt bei echten Besuchern leer. */
  website?: string;
}

/**
 * Schickt eine Anfrage an den Posteingang der Anwendung.
 *
 * Den Auftritt hängt diese Funktion selbst an: Er steht am Host und nicht am
 * Formular, und jedes Formular, das ihn selbst mitschicken müsste, wäre ein
 * Formular, das ihn vergessen kann.
 *
 * @throws Error mit der Meldung des Servers, wenn die Anfrage abgelehnt wurde
 *         (fehlende Angaben, zu viele Anfragen). Aufrufer zeigen die Meldung direkt an.
 */
export async function submitContactRequest(payload: ContactSubmission): Promise<void> {
  try {
    await publicApi.post('/contact-requests', { ...payload, site: REQUEST_SITE[activeSiteKey()] });
  } catch (error: any) {
    const serverMessage = error?.response?.data?.error;
    if (serverMessage) throw new Error(serverMessage);
    throw new Error(
      'Ihre Nachricht konnte gerade nicht übermittelt werden. Bitte versuchen Sie es später erneut oder rufen Sie uns an.',
    );
  }
}

export default publicApi;
